import React from "react";
import { CcButton, CcIconButton } from "../mui";
import {
  CcButtonIcon,
  CcIconDocumentAdd,
  CcIconDocumentEdit,
  CcIconEdit,
  CcIconTrash,
} from "../../icons";
import { cx } from "../utils";
import CcTooltip from "./CcTooltip";

/**
 * CcCarePlanFormActions — care plan assessment header toolbar.
 * Uses themed CcButton / CcIconButton per Zen Editorial v2.1.
 *
 * ONE blue primary, always last before Archive (header Option B, 24 Sep 2026):
 * Edit / New Version normally; the review takes over when one is due or kept,
 * and the edit action steps back to secondary in front of it.
 *
 * | Control        | Component     | Variant            | Icon              |
 * |----------------|---------------|--------------------|-------------------|
 * | View document  | CcIconButton  | secondary (sm)     | CcIconDocumentEdit|
 * | Edit           | CcButton      | primary/secondary  | CcIconEdit        |
 * | + New Version  | CcButton      | primary/secondary  | CcIconDocumentAdd |
 * | Start review   | CcButton      | primary/secondary  | CcIconDocumentAdd |
 * | Archive        | CcIconButton  | danger outline     | CcIconTrash       |
 */
const CcCarePlanFormActions = ({
  className,
  showDocument = true,
  onViewDocument,
  showEdit = false,
  onEdit,
  canCreateVersion = false,
  hasDraftInProgress = false,
  onContinueDraft,
  onCreateNewVersion,
  showEvaluate = false,
  onEvaluate,
  // "Resume review" when a half-done review is kept; primary when a review is
  // due or kept (Edit / New Version then step back to secondary).
  evaluateLabel = "Start review",
  evaluatePrimary = false,
  showArchive = false,
  onArchive,
  isVersioningApplicable = true,
}) => {
  const reviewLeads = showEvaluate && evaluatePrimary;

  const editAction = showEdit && onEdit && (
    <CcButton
      key="edit"
      variant={reviewLeads ? "secondary" : "primary"}
      size="sm"
      startIcon={
        <CcButtonIcon>
          <CcIconEdit size={16} />
        </CcButtonIcon>
      }
      onClick={onEdit}
    >
      Edit
    </CcButton>
  );

  const versionAction =
    canCreateVersion &&
    isVersioningApplicable &&
    (hasDraftInProgress ? (
      <CcButton
        key="version"
        variant="secondary"
        size="sm"
        startIcon={
          <CcButtonIcon>
            <CcIconEdit size={16} />
          </CcButtonIcon>
        }
        onClick={onContinueDraft}
      >
        Continue Draft
      </CcButton>
    ) : (
      <CcButton
        key="version"
        variant={reviewLeads ? "secondary" : "primary"}
        size="sm"
        startIcon={
          <CcButtonIcon>
            <CcIconDocumentAdd size={16} />
          </CcButtonIcon>
        }
        onClick={onCreateNewVersion}
      >
        New Version
      </CcButton>
    ));

  const reviewAction = showEvaluate && (
    <CcButton
      key="review"
      variant={reviewLeads ? "primary" : "secondary"}
      size="sm"
      startIcon={
        <CcButtonIcon>
          <CcIconDocumentAdd size={16} />
        </CcButtonIcon>
      }
      onClick={onEvaluate}
    >
      {evaluateLabel}
    </CcButton>
  );

  // The primary always sits last, next to Archive.
  const ordered = reviewLeads
    ? [editAction, versionAction, reviewAction]
    : [reviewAction, editAction, versionAction];

  return (
    <div className={cx("cc-care-plan-form-actions", className)}>
      {showDocument && onViewDocument && (
        <CcIconButton
          label="View document"
          variant="secondary"
          size="sm"
          onClick={onViewDocument}
        >
          <CcIconDocumentEdit size={18} />
        </CcIconButton>
      )}

      {ordered}

      {showArchive && onArchive && (
        // Icon only; the caller keeps its confirm step. The span carries the
        // tooltip's ref (CcIconButton is a plain function component).
        <CcTooltip title="Archive plan" placement="bottom">
          <span className="cc-care-plan-form-actions__archive">
            <CcIconButton
              label="Archive plan"
              title={undefined}
              variant="secondary"
              size="sm"
              onClick={onArchive}
            >
              <CcIconTrash size={16} />
            </CcIconButton>
          </span>
        </CcTooltip>
      )}
    </div>
  );
};

export default CcCarePlanFormActions;
